import { Sender } from "../components/Sender";

/**
 * Resolves the effective senders for a prospect by applying the Layer 3 override logic:
 * - If the prospect has dedicated sender overrides (listPerson.senders), use those.
 * - Otherwise, fall back to campaign-level senders (from campaignConfig).
 *
 * @param prospectSenders - Senders from listPerson.senders (prospect-level override)
 * @param campaignSenders - Senders from the resolved campaign config
 * @returns The effective senders to use, or undefined if neither source has senders
 */
export const resolveEffectiveSenders = (
	prospectSenders: Sender[] | null | undefined,
	campaignSenders: Sender[] | undefined
): Sender[] | undefined => {
	if (prospectSenders && prospectSenders.length > 0) {
		return prospectSenders;
	}

	return campaignSenders;
};

/**
 * Merges effective senders with the full list of default senders (all available sending accounts).
 * Default senders not present in the effective list are prepended so the UI shows all possible senders,
 * with the effective ones carrying their configured active/deactivated state.
 *
 * @param effectiveSenders - The resolved senders (from resolveEffectiveSenders)
 * @param defaultSenders - All available sending accounts with default state (active: false)
 * @returns The merged list: defaults not in effective list + effective senders, or just defaults if no effective senders
 */
export const mergeWithDefaultSenders = (
	effectiveSenders: Sender[] | undefined,
	defaultSenders: Sender[]
): Sender[] => {
	if (!effectiveSenders || effectiveSenders.length === 0) {
		return defaultSenders;
	}

	const defaultSendersNotInEffective = defaultSenders.filter(
		(sender) =>
			!effectiveSenders.find(
				(s) =>
					s.userId === sender.userId &&
					s.type === sender.type &&
					s.emailAddress === sender.emailAddress
			)
	);

	return [...defaultSendersNotInEffective, ...effectiveSenders];
};
